/**
 * Motor de SFX de speak. Sintetiza cada sonido con Web Audio (ver `synth.ts`),
 * **cachea** el `AudioBuffer` (la síntesis se paga una sola vez por sonido) y lo
 * reproduce por un `AudioContext` DEDICADO — nunca el `AudioPlayer` del pipeline
 * de voz. Compartir el context de la voz rompería el gate del mic
 * (`AudioPlayer.isPlaying()`), la animación de la orb (RMS) y el barge-in.
 *
 * Gating: los sonidos `live` (suenan durante el turno de voz) se suprimen en
 * modo realtime con turno activo, donde el mic manda audio siempre y un SFX por
 * los parlantes dispararía un barge-in falso del server-VAD.
 *
 * Mute: pref device-local `orborix.sfxMuted` (ver `@orborix/voice-client`
 * `preferences.ts`), NO en la DB — mismo criterio que el volumen de TTS.
 */
import { readSfxMuted, readSfxVolume } from '@orborix/voice-client';
import { SFX_SPECS, type SfxName, type SoundSpec } from './sounds.js';
import { renderSpec, canRender } from './synth.js';
import { isAudioStuck } from '../audioStuck.js';

/**
 * ¿Se debe suprimir este sonido dado el estado de la voz? Solo aplica a
 * sonidos `live`: en realtime con turno activo, un SFX por los parlantes puede
 * gatillar un barge-in falso (el mic manda audio siempre). Los sonidos discretos
 * de UI nunca se gatean acá (solo dependen del mute). Puro y testeable.
 */
export function shouldGate(spec: SoundSpec, voiceActive: boolean, realtime: boolean): boolean {
  if (!spec.live) return false;
  return realtime && voiceActive;
}

/** Sonidos frecuentes que se pre-renderizan al crear el context (evita hitch). */
const WARM_ON_INIT: readonly SfxName[] = [
  'correct',
  'almost',
  'celebrate-small',
  'session-start',
  'session-end',
  'card-flip',
];

class SfxEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly cache = new Map<SfxName, Promise<AudioBuffer>>();
  private muted = false;
  private volume = 1;
  private voiceActive = false;
  private realtime = false;
  private gestureBound = false;

  constructor() {
    if (typeof window === 'undefined') return;
    this.muted = readSfxMuted();
    this.volume = readSfxVolume();
    window.addEventListener('orborix:pref.sfxMuted', (e: Event) => {
      const detail = (e as CustomEvent<boolean>).detail;
      if (typeof detail === 'boolean') this.muted = detail;
    });
    window.addEventListener('orborix:pref.sfxVolume', (e: Event) => {
      const detail = (e as CustomEvent<number>).detail;
      if (typeof detail !== 'number') return;
      this.volume = Math.min(1, Math.max(0, detail));
      if (this.master) this.master.gain.value = this.volume;
    });
  }

  /**
   * Estado del turno de voz para el gating de sonidos `live`. `voiceActive` =
   * hay un turno en curso (no `idle`); `realtime` = modo S2S full-duplex.
   */
  setVoiceContext(voiceActive: boolean, realtime: boolean): void {
    this.voiceActive = voiceActive;
    this.realtime = realtime;
  }

  play(name: SfxName): void {
    // Audio del sistema trabado: crear el context congelaría la pestaña.
    if (this.muted || isAudioStuck()) return;
    const spec = SFX_SPECS[name];
    if (shouldGate(spec, this.voiceActive, this.realtime)) return;
    const ctx = this.audioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    void this.buffer(name)
      .then((buf) => {
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(this.master ?? ctx.destination);
        src.start();
      })
      .catch(() => {
        // Entorno sin OfflineAudioContext (SSR/test): best-effort, no rompemos
        // la UI. Una spec malformada ya habría fallado al importar `sounds.ts`.
      });
  }

  private buffer(name: SfxName): Promise<AudioBuffer> {
    let p = this.cache.get(name);
    if (!p) {
      p = renderSpec(SFX_SPECS[name]);
      this.cache.set(name, p);
    }
    return p;
  }

  private warm(): void {
    if (this.muted || !canRender()) return;
    for (const n of WARM_ON_INIT) void this.buffer(n);
  }

  private audioCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (typeof window === 'undefined') return null;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    // Gain maestro dedicado: aplica el volumen de SFX sin tocar la voz.
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.ctx.destination);
    this.bindGesture();
    this.warm();
    return this.ctx;
  }

  private bindGesture(): void {
    if (this.gestureBound || typeof window === 'undefined') return;
    this.gestureBound = true;
    const unlock = (): void => {
      void this.ctx?.resume();
    };
    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  }
}

/** Singleton del motor de SFX. Usar `useSfx()` en componentes React. */
export const sfx = new SfxEngine();
